import type React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import type { CageType, DomainFishType } from '@/domain/cages';
import { useGetCageTypes, useGetFishTypes, useUpdateCage } from '@/domain/cages';
import { allowedCageTypes, isSmoltLocation } from '@/utils/locationTypeHelpers';

import {
    type FormState,
    type FormStatus,
    INITIAL_STATUS,
    makeInitialFormState,
    type SettingsFormProps,
} from './settingsFormState';

export interface CageSettingsForm {
    formState: FormState;
    status: FormStatus;
    visibleCageTypes: CageType[];
    fishTypesList: DomainFishType[];
    isLocked: boolean;
    handleNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleCageTypeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    handleFishTypeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    handleExternalNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleExternalIdChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleSubmit: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
}

/** The cage settings form's state, its option lists, and the save. */
export function useCageSettingsForm(props: SettingsFormProps): CageSettingsForm {
    const { locationTypeShortName } = props;
    const { t } = useTranslation('cageSettings');

    const queryClient = useQueryClient();

    const [formState, setFormState] = useState<FormState>(() => makeInitialFormState(props));
    const [status, setStatus] = useState<FormStatus>(INITIAL_STATUS);

    const { data: cageTypesList } = useGetCageTypes();
    const { data: fishTypesList } = useGetFishTypes();
    const visibleCageTypes = allowedCageTypes(cageTypesList, locationTypeShortName);
    const isLocked = isSmoltLocation(locationTypeShortName);

    const { mutate: updateCage } = useUpdateCage({
        onSuccess: () => {
            setStatus({
                isSubmitting: false,
                error: '',
                successMessage: t('settings.updateSuccess'),
            });
            queryClient.invalidateQueries(); // Invalidate everything — cage name may affect many queries
        },
        onError: (err: unknown) => {
            setStatus({
                isSubmitting: false,
                error: apiErrorContent(err, undefined, (message) =>
                    t('settings.updateError', { message })
                ),
                successMessage: '',
            });
        },
    });

    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setFormState((prev) => ({ ...prev, name: { ...prev.name, value: e.target.value } }));
    }, []);

    const handleCageTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const selected = Number.parseInt(e.target.value, 10);
        setFormState((prev) => ({
            ...prev,
            // -1 is the placeholder option, so it means "nothing chosen" rather than an id.
            cageType: {
                ...prev.cageType,
                value: Number.isSafeInteger(selected) && selected > 0 ? selected : undefined,
            },
        }));
    }, []);

    const handleFishTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        const selected = Number.parseInt(e.target.value, 10);
        setFormState((prev) => ({
            ...prev,
            // -1 is the placeholder option, so it means "nothing chosen" rather than an id.
            fishType: {
                ...prev.fishType,
                value: Number.isSafeInteger(selected) && selected > 0 ? selected : undefined,
            },
        }));
    }, []);

    const handleExternalNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setFormState((prev) => ({
            ...prev,
            externalName: { ...prev.externalName, value: e.target.value },
        }));
    }, []);

    const handleExternalIdChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setFormState((prev) => ({
            ...prev,
            externalId: { ...prev.externalId, value: e.target.value },
        }));
    }, []);

    const handleSubmit = useCallback(
        (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
            e.preventDefault();
            e.stopPropagation();

            setStatus((prev) => ({ ...prev, isSubmitting: true, error: '', successMessage: '' }));

            updateCage({
                cageId: formState.id,
                requestBody: {
                    name: formState.name.value,
                    // Omitted when nothing is chosen, rather than sent as a placeholder 0 or -1
                    // that would fail the `fk_cagetypeid` constraint and lose the whole edit.
                    cageTypeId: formState.cageType.value,
                    // Same reasoning: omitted rather than sent as a placeholder, so a pen with
                    // no species on record keeps none instead of failing the FK.
                    fishTypeId: formState.fishType.value,
                    externalName: formState.externalName.value || '',
                    externalId: formState.externalId.value || '',
                },
            });
        },
        [formState, updateCage]
    );

    return {
        formState,
        status,
        visibleCageTypes,
        fishTypesList,
        isLocked,
        handleNameChange,
        handleCageTypeChange,
        handleFishTypeChange,
        handleExternalNameChange,
        handleExternalIdChange,
        handleSubmit,
    };
}
